import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  BookingCommissionItem,
  BookingRefundItem,
  BookingListItem,
  CustomerPaymentItem,
  ExpenseRequestItem,
  SaveCommissionRequest,
  SaveExpenseRequest,
  SavePaymentRequest,
  SaveRefundRequest,
  SaveTourMemberRequest,
  TourMemberItem,
} from './booking.service';

export interface GroupBookingListItem {
  id: string;
  bookingNo: string;
  tourName: string;
  tourCode: string | null;
  country: string | null;
  bookingDate: string;
  departureDate: string;
  returnDate: string | null;
  pax: number;
  totalAmount: number | string;
  pricePerPerson: number | string;
  discount?: number | string | null;
  paymentStatus: 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERPAID';
  status: 'DRAFT' | 'PENDING_CONFIRMATION' | 'CONFIRMED' | 'COMPLETED' | 'REFUND_PENDING' | 'REFUND' | 'CANCELLED' | 'DELETED';
  booking_customer: { id: string; customerCode: string; name: string };
  booking_payments: Array<{ id: string }>;
}

export interface SaveGroupBookingRequest {
  tourId?: string;
  customerId: string;
  customerIsTraveler: boolean;
  tourName: string;
  tourCode: string;
  country: string;
  departureDate: string;
  returnDate: string;
  pax: number;
  tourPrice: number;
  discount: number;
}

@Injectable({ providedIn: 'root' })
export class GroupBookingService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/booking-groups`;

  async getGroupBookings(): Promise<GroupBookingListItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ bookings: GroupBookingListItem[] }>(this.apiUrl, {
        withCredentials: true,
      }),
    );
    return response.bookings;
  }

  async getById(id: string): Promise<BookingListItem | undefined> {
    const response = await firstValueFrom(
      this.http.get<{ booking: BookingListItem | undefined }>(`${this.apiUrl}/${id}`, {
        withCredentials: true,
      }),
    );
    return response.booking;
  }

  async createGroupBooking(input: SaveGroupBookingRequest): Promise<GroupBookingListItem> {
    const response = await firstValueFrom(
      this.http.post<{ message: string; booking: GroupBookingListItem }>(this.apiUrl, input, {
        withCredentials: true,
      }),
    );
    return response.booking;
  }

  async updateGroupBooking(
    id: string,
    input: SaveGroupBookingRequest,
  ): Promise<GroupBookingListItem> {
    const response = await firstValueFrom(
      this.http.put<{ message: string; booking: GroupBookingListItem }>(
        `${this.apiUrl}/${id}`,
        input,
        { withCredentials: true },
      ),
    );
    return response.booking;
  }

  async deleteGroupBooking(id: string): Promise<GroupBookingListItem> {
    const response = await firstValueFrom(
      this.http.delete<{ message: string; booking: GroupBookingListItem }>(`${this.apiUrl}/${id}`, {
        withCredentials: true,
      }),
    );
    return response.booking;
  }

  async closeCase(id: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.apiUrl}/${id}/close-case`, {}, { withCredentials: true }),
    );
  }

  createTraveler(
    bookingId: string,
    input: SaveTourMemberRequest,
    passport: File,
  ): Promise<TourMemberItem> {
    return this.saveTraveler(`${this.apiUrl}/${bookingId}/travelers`, input, passport, 'post');
  }

  updateTraveler(
    bookingId: string,
    travelerId: string,
    input: SaveTourMemberRequest,
    passport: File | null,
  ): Promise<TourMemberItem> {
    return this.saveTraveler(
      `${this.apiUrl}/${bookingId}/travelers/${travelerId}`,
      input,
      passport,
      'put',
    );
  }

  private async saveTraveler(
    url: string,
    input: SaveTourMemberRequest,
    passport: File | null,
    method: 'post' | 'put',
  ): Promise<TourMemberItem> {
    const formData = new FormData();
    formData.append('name', input.name);
    formData.append('idcard', input.idcard);
    formData.append('phone', input.phone);
    formData.append('isLeadTraveler', String(input.isLeadTraveler));
    if (passport) formData.append('passport', passport);
    const response = await firstValueFrom(
      this.http.request<{ traveler: TourMemberItem }>(method, url, {
        body: formData,
        withCredentials: true,
      }),
    );
    return response.traveler;
  }

  async deleteTraveler(bookingId: string, travelerId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/travelers/${travelerId}`, {
        withCredentials: true,
      }),
    );
  }

  createPayment(
    bookingId: string,
    input: SavePaymentRequest,
    slip: File,
  ): Promise<CustomerPaymentItem> {
    return this.savePayment(`${this.apiUrl}/${bookingId}/payments`, input, slip, 'post');
  }

  updatePayment(
    bookingId: string,
    paymentId: string,
    input: SavePaymentRequest,
    slip: File | null,
  ): Promise<CustomerPaymentItem> {
    return this.savePayment(
      `${this.apiUrl}/${bookingId}/payments/${paymentId}`,
      input,
      slip,
      'put',
    );
  }

  private async savePayment(
    url: string,
    input: SavePaymentRequest,
    slip: File | null,
    method: 'post' | 'put',
  ): Promise<CustomerPaymentItem> {
    const formData = new FormData();
    formData.append('payerName', input.payerName);
    formData.append('paymentStage', input.paymentStage);
    formData.append('paymentMethod', input.paymentMethod);
    formData.append('creditCardFeePercent', String(input.creditCardFeePercent));
    if (input.creditCardFeeResponsibility) {
      formData.append('creditCardFeeResponsibility', input.creditCardFeeResponsibility);
    }
    formData.append('amount', String(input.amount));
    formData.append('withholdingTaxAmount', String(input.withholdingTaxAmount));
    formData.append('paidDate', input.paidDate);
    formData.append('paymentTime', input.paymentTime);
    if (slip) formData.append('paymentSlip', slip);
    const response = await firstValueFrom(
      this.http.request<{ payment: CustomerPaymentItem }>(method, url, {
        body: formData,
        withCredentials: true,
      }),
    );
    return response.payment;
  }

  async deletePayment(bookingId: string, paymentId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/payments/${paymentId}`, {
        withCredentials: true,
      }),
    );
  }

  async createRefund(
    bookingId: string,
    input: SaveRefundRequest,
    attachment: File,
  ): Promise<BookingRefundItem> {
    const formData = new FormData();
    formData.append('refundName', input.refundName);
    formData.append('bankName', input.bankName);
    formData.append('accountNumber', input.accountNumber);
    formData.append('refundType', input.refundType);
    formData.append('amount', String(input.amount));
    formData.append('reason', input.reason);
    formData.append('refundAttachment', attachment);
    const response = await firstValueFrom(
      this.http.post<{ refund: BookingRefundItem }>(`${this.apiUrl}/${bookingId}/refunds`, formData, {
        withCredentials: true,
      }),
    );
    return response.refund;
  }

  async createExpense(
    bookingId: string,
    input: SaveExpenseRequest,
    attachment: File,
  ): Promise<ExpenseRequestItem> {
    return this.saveExpense(`${this.apiUrl}/${bookingId}/expenses`, input, attachment, 'post');
  }

  async updateExpense(
    bookingId: string,
    expenseId: string,
    input: SaveExpenseRequest,
    attachment: File | null,
  ): Promise<ExpenseRequestItem> {
    return this.saveExpense(
      `${this.apiUrl}/${bookingId}/expenses/${expenseId}`,
      input,
      attachment,
      'put',
    );
  }

  private async saveExpense(
    url: string,
    input: SaveExpenseRequest,
    attachment: File | null,
    method: 'post' | 'put',
  ): Promise<ExpenseRequestItem> {
    const formData = new FormData();
    formData.append('expenseNameSnapshot', input.expenseNameSnapshot);
    if (input.wholesaleId) formData.append('wholesaleId', input.wholesaleId);
    formData.append('description', input.description);
    formData.append('actualTransferAmount', String(input.actualTransferAmount));
    formData.append('submitForReview', String(input.submitForReview));
    if (attachment) formData.append('expenseAttachment', attachment);
    const response = await firstValueFrom(
      this.http.request<{ expense: ExpenseRequestItem }>(method, url, {
        body: formData,
        withCredentials: true,
      }),
    );
    return response.expense;
  }

  async deleteExpense(bookingId: string, expenseId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/expenses/${expenseId}`, {
        withCredentials: true,
      }),
    );
  }

  async submitExpenseReceipt(
    bookingId: string,
    expenseId: string,
    receipt: File,
  ): Promise<ExpenseRequestItem> {
    const formData = new FormData();
    formData.append('expenseReceipt', receipt);
    const response = await firstValueFrom(
      this.http.post<{ expense: ExpenseRequestItem }>(
        `${this.apiUrl}/${bookingId}/expenses/${expenseId}/receipt`,
        formData,
        { withCredentials: true },
      ),
    );
    return response.expense;
  }

  createCommission(
    bookingId: string,
    input: SaveCommissionRequest,
  ): Promise<BookingCommissionItem> {
    return this.saveCommission(`${this.apiUrl}/${bookingId}/commissions`, input, 'post');
  }

  updateCommission(
    bookingId: string,
    commissionId: string,
    input: SaveCommissionRequest,
  ): Promise<BookingCommissionItem> {
    return this.saveCommission(
      `${this.apiUrl}/${bookingId}/commissions/${commissionId}`,
      input,
      'put',
    );
  }

  private async saveCommission(
    url: string,
    input: SaveCommissionRequest,
    method: 'post' | 'put',
  ): Promise<BookingCommissionItem> {
    const response = await firstValueFrom(
      this.http.request<{ commission: BookingCommissionItem }>(method, url, {
        body: input,
        withCredentials: true,
      }),
    );
    return response.commission;
  }

  async deleteCommission(bookingId: string, commissionId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/commissions/${commissionId}`, {
        withCredentials: true,
      }),
    );
  }

  async deleteRefund(bookingId: string, refundId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/refunds/${refundId}`, {
        withCredentials: true,
      }),
    );
  }

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }
}
